import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { getUsers } from 'services/users'

import debounce from 'utils/debounce'

const defaultParams = {
  itemCount: 0,
  pageSize: 50,
  pageOffset: 0,
  page: 0,
}

const defaultFilters = {
  nameTerm: null,
  customerIds: null,
}

export const USERS_QUERY_KEY = 'usersQuery'

function useUsers() {
  const [pagination, setPagination] = useState(defaultParams)
  const [filters, setFilters] = useState(defaultFilters)
  const [customers, setCustomers] = useState([])

  // Create a debounced version of getUsers
  const debouncedGetUsers = useMemo(() => debounce(getUsers, 300), [])

  const { isLoading, data: users } = useQuery(
    [USERS_QUERY_KEY, pagination, filters],
    () =>
      debouncedGetUsers({
        nameTerm: filters.nameTerm,
        customerIds: filters.customerIds,
        pageSize: pagination.pageSize,
        pageOffset: pagination.pageOffset,
      }),
    {
      refetchOnWindowFocus: false,
      useErrorBoundary: true,
      // Disable structural sharing to ensure fresh data is used when users are edited
      // React Query's structural sharing was preventing updated user data from showing
      // after invalidateQueries() because it considered the user list "structurally similar"
      structuralSharing: false,
      onSuccess: data => {
        setPagination(prevState => ({
          ...prevState,
          pageOffset: data.meta.pageOffset,
          pageSize: data.meta.pageSize,
          itemCount: data.meta.itemCount,
        }))
      },
    },
  )

  const handlePageChange = newPage => {
    const { page, pageSize } = pagination
    if (newPage > page) {
      setPagination(prevState => ({
        ...prevState,
        page: page + 1,
        pageOffset: (page + 1) * pageSize,
      }))
    } else if (newPage < page) {
      setPagination(prevState => ({
        ...prevState,
        page: page - 1,
        pageOffset: (page - 1) * pageSize,
      }))
    }
  }

  const handleRowsChange = pageSize => {
    setPagination(prevState => ({ ...prevState, page: 0, pageSize: pageSize, pageOffset: 0 }))
  }

  async function handleUserNameSearch(e) {
    const nameTerm = e.target.value || null
    setFilters(prevState => ({ ...prevState, nameTerm }))
  }

  async function handleCustomerSearch(customers) {
    const customerIds = customers.map(customer => customer.id)
    setFilters(prevState => ({ ...prevState, customerIds }))
    setCustomers(customers)
  }

  return {
    users,
    customers,
    pagination,
    isLoading,
    filters,
    handlePageChange,
    handleRowsChange,
    handleUserNameSearch,
    handleCustomerSearch,
  }
}

export { useUsers }
